import React, { useState, useEffect, useRef } from 'react';
import './CannonLaunchGame.css';

/**
 * NEW COORDINATE SYSTEM & GENERATION LOGIC:
 * - We define one coordinate system (“game coordinates”) in which:
 *     • Ground is at y = 0.
 *     • All positions (projectile, obstacles, etc.) are stored in game units.
 * - A constant SCALE (pixels per game unit) converts game coordinates to CSS pixels.
 */
const SCALE = 2;             // pixels per game unit
const GROUND_Y = 0;          // Game coordinate of the ground
const GROUND_RENDER_HEIGHT = 100;  // Height (in pixels) of the ground element

// Physics & gameplay constants (all distances now in game units)
const GRAVITY = -180;         // Acceleration downward (game units/sec²)
const FRICTION_COEFF = 0.7;  // Bounce energy loss factor
const STOP_THRESHOLD = 1;    // When speeds drop below this, we end the flight

const ANGLE_ANIM_SPEED = 2.0;  // For animating angle selection (degrees per frame)
const POWER_ANIM_SPEED = 3.0;  // For animating power selection (percent per frame)
const MAX_LAUNCH_SPEED = 800; // Maximum launch speed (game units/sec) – try 8000 for testing

// Terrain & obstacle generation (all distances in game units)
const INITIAL_WORLD_WIDTH = 5000;
const GENERATION_CHUNK_WIDTH = 5000; // width of each new chunk (game units)
const MIN_GENERATION_DISTANCE = 5000; // when player is within this distance to the generated end, add more
const CLEANUP_OFFSET = 5000; // remove obstacles that are behind (game units)

// Tree constants
const TREE_SPACING = 200;      // minimum spacing between trees (game units)
const TREE_COLLISION_WIDTH = 40; // collision “width” (game units)
const TREE_HEIGHT = 100;       // base tree height (game units)
const TREE_COLLISION_SLOWDOWN = 0.7;

// Bomb constants
const BOMB_SPACING = 800;
const BOMB_BOOST_SPEED = 500;
const BOMB_COLLISION_WIDTH = 30;

// Trampoline constants
const TRAMPOLINE_SPACING = 600;
const TRAMPOLINE_WIDTH = 60;
const TRAMPOLINE_BOOST = 300;

// Hole constants
const HOLE_SPACING = 1000;
const HOLE_WIDTH = 100;        // base width (game units)

// UFO constants (unchanged in spirit)
const UFO_SPEED = 1500;
const UFO_BOOST_SPEED = 600;
const UFO_SPAWN_INTERVAL = 5000;
const UFO_HEIGHT_RANGE = 800;
const UFO_COLLISION_WIDTH = 160;
const UFO_COLLISION_HEIGHT = 160;

// Emoticons
const EMOTICONS = ['😀', '😁', '😆', '🤣', '😇', '😋', '😝', '🤪', '😟', '😭', '😡', '😲', '🤮'];

function CannonLaunchGame({ setIsTimerActive, gamePaused, soundEffects }) {
  // ------------------
  // GAME STATE & REFS
  // ------------------
  const [gameState, setGameState] = useState('angle');
  // 'angle' => animate angle, 'power' => animate power, 'flying' => in-flight, 'done' => game over

  const [angle, setAngle] = useState(0); // in degrees (0: right, 90: up)
  const angleRef = useRef(0);
  const angleDirRef = useRef(1);         // 1: increasing, -1: decreasing

  const [power, setPower] = useState(0);   // 0..100%
  const powerRef = useRef(0);
  const powerDirRef = useRef(1);

  const requestRef = useRef(null);

  // Projectile (stick figure) state in game coordinates
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });
  const [velocity, setVelocity] = useState({ vx: 0, vy: 0 });
  const velocityRef = useRef({ vx: 0, vy: 0 });
  const [projRotation, setProjRotation] = useState(0);
  const projRotationRef = useRef(0);

  // Horizontal camera (in game units)
  const [cameraX, setCameraX] = useState(0);
  const cameraXRef = useRef(0);

  // Score
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('cannonHighScore');
    return saved ? parseFloat(saved) : 0;
  });

  // Terrain/obstacles – stored in game coordinates
  const [trees, setTrees] = useState([]);
  const treesRef = useRef([]);
  const [bombs, setBombs] = useState([]);
  const bombsRef = useRef([]);
  const [trampolines, setTrampolines] = useState([]);
  const trampolinesRef = useRef([]);
  const [holes, setHoles] = useState([]);
  const holesRef = useRef([]);

  // For UFOs (positions in game coordinates)
  const [ufos, setUfos] = useState([]);
  const ufosRef = useRef([]);

  // Other refs
  const fallingInHoleRef = useRef(false);
  const lastEmoticonChangeRef = useRef(0);
  const [currentEmoticon, setCurrentEmoticon] = useState(EMOTICONS[0]);

  // We keep track of how far we’ve generated terrain (in game units)
  const lastGeneratedXRef = useRef(INITIAL_WORLD_WIDTH);

  // ---------------------------
  // HELPER FUNCTIONS (Conversion)
  // ---------------------------
  // Convert game coordinates to CSS pixel values.
  function gameToCSS({ x, y }) {
    return { left: `${x * SCALE}px`, bottom: `${y * SCALE}px` };
  }

  // ---------------------------
  // OBSTACLE GENERATION FUNCTIONS
  // ---------------------------
  const generateTreesInRange = (startX, endX) => {
    const newTrees = [];
    let x = startX;
    while (x < endX) {
      x += TREE_SPACING + Math.random() * TREE_SPACING;
      newTrees.push({
        x, 
        scale: 0.7 + Math.random() * 0.6,
        rotation: (Math.random() * 10) - 5,
        isKnockedOver: false,
        y: 0  // trees are on the ground
      });
    }
    return newTrees;
  };

  const generateBombsInRange = (startX, endX) => {
    const newBombs = [];
    let x = startX;
    while (x < endX) {
      x += BOMB_SPACING + Math.random() * BOMB_SPACING;
      newBombs.push({
        x,
        y: 0,  // on the ground
        isExploded: false,
        scale: 0.8 + Math.random() * 0.4
      });
    }
    return newBombs;
  };

  const generateTrampolinesInRange = (startX, endX) => {
    const newTrampolines = [];
    let x = startX;
    while (x < endX) {
      x += TRAMPOLINE_SPACING + Math.random() * TRAMPOLINE_SPACING;
      newTrampolines.push({
        x,
        scale: 0.8 + Math.random() * 0.4,
        isUsed: false,
        y: 0  // on the ground
      });
    }
    return newTrampolines;
  };

  const generateHolesInRange = (startX, endX) => {
    const newHoles = [];
    let x = startX;
    while (x < endX) {
      x += HOLE_SPACING + Math.random() * HOLE_SPACING;
      newHoles.push({
        x,
        width: HOLE_WIDTH + Math.random() * 50  // variation in hole width
      });
    }
    return newHoles;
  };

  // ---------------------------
  // EMOTICON CHANGER
  // ---------------------------
  const getRandomEmoticon = () => {
    return EMOTICONS[Math.floor(Math.random() * EMOTICONS.length)];
  };

  const tryChangeEmoticon = () => {
    const now = Date.now();
    if (now - lastEmoticonChangeRef.current >= 500) {
      setCurrentEmoticon(getRandomEmoticon());
      lastEmoticonChangeRef.current = now;
    }
  };

  // ---------------------------
  // ANIMATION LOOP FOR ANGLE & POWER
  // ---------------------------
  const animLoop = () => {
    if (gameState === 'angle') {
      let newAngle = angleRef.current + ANGLE_ANIM_SPEED * angleDirRef.current;
      if (newAngle > 90) {
        newAngle = 90;
        angleDirRef.current = -1;
      } else if (newAngle < 0) {
        newAngle = 0;
        angleDirRef.current = 1;
      }
      angleRef.current = newAngle;
      setAngle(newAngle);
    } else if (gameState === 'power') {
      let newPower = powerRef.current + POWER_ANIM_SPEED * powerDirRef.current;
      if (newPower > 100) {
        newPower = 100;
        powerDirRef.current = -1;
      } else if (newPower < 0) {
        newPower = 0;
        powerDirRef.current = 1;
      }
      powerRef.current = newPower;
      setPower(newPower);
    }
    requestRef.current = requestAnimationFrame(animLoop);
  };

  // ---------------------------
  // PHYSICS LOOP (game coordinates)
  // ---------------------------
  const physicsLoop = () => {
    const dt = 1 / 60;
    let { vx, vy } = velocityRef.current;
    let { x, y } = positionRef.current;

    // Update score (we use x in game units; you can scale as desired)
    const currentScore = Math.floor(x);
    setScore(currentScore);
    if (currentScore > highScore) {
      setHighScore(currentScore);
      localStorage.setItem('cannonHighScore', currentScore.toString());
    }

    // ---------------------------
    // 1. GENERATE NEW TERRAIN CHUNKS
    // ---------------------------
    if (x > lastGeneratedXRef.current - MIN_GENERATION_DISTANCE) {
      const newStartX = lastGeneratedXRef.current;
      const newEndX = newStartX + GENERATION_CHUNK_WIDTH;

      treesRef.current = [...treesRef.current, ...generateTreesInRange(newStartX, newEndX)];
      bombsRef.current = [...bombsRef.current, ...generateBombsInRange(newStartX, newEndX)];
      trampolinesRef.current = [...trampolinesRef.current, ...generateTrampolinesInRange(newStartX, newEndX)];
      holesRef.current = [...holesRef.current, ...generateHolesInRange(newStartX, newEndX)];

      lastGeneratedXRef.current = newEndX;

      // Update the world & ground widths in CSS
      const worldDiv = document.querySelector('.cannon-game-world');
      const groundDiv = document.querySelector('.cannon-ground');
      if (worldDiv && groundDiv) {
        const newWidth = (newEndX + GENERATION_CHUNK_WIDTH) * SCALE;
        worldDiv.style.width = `${newWidth}px`;
        groundDiv.style.width = `${newWidth}px`;
      }
    }

    // ---------------------------
    // 2. CLEAN UP OLD OBSTACLES (that fall far behind)
    // ---------------------------
    const cleanupThreshold = x - CLEANUP_OFFSET;
    treesRef.current = treesRef.current.filter(tree => tree.x >= cleanupThreshold);
    bombsRef.current = bombsRef.current.filter(bomb => bomb.x >= cleanupThreshold);
    trampolinesRef.current = trampolinesRef.current.filter(tramp => tramp.x >= cleanupThreshold);
    holesRef.current = holesRef.current.filter(hole => hole.x >= cleanupThreshold);

    setTrees([...treesRef.current]);
    setBombs([...bombsRef.current]);
    setTrampolines([...trampolinesRef.current]);
    setHoles([...holesRef.current]);

    // ---------------------------
    // 3. PHYSICS INTEGRATION
    // ---------------------------
    vy += GRAVITY * dt;
    x += vx * dt;
    y += vy * dt;

    // ---------------------------
    // 4. COLLISION DETECTIONS
    // (For simplicity, we assume obstacles are on the ground (y=0) and collisions occur when the stick figure is near ground.)
    // ---------------------------

    // Tree collision (if stick figure is near ground and horizontally close)
    const treeHitIndex = treesRef.current.findIndex(tree => {
      if (tree.isKnockedOver) return false;
      return (Math.abs(x - tree.x) < TREE_COLLISION_WIDTH / 2) && (y < 1);
    });
    if (treeHitIndex !== -1) {
      treesRef.current[treeHitIndex] = { ...treesRef.current[treeHitIndex], rotation: 90, isKnockedOver: true };
      setTrees([...treesRef.current]);
      vx *= TREE_COLLISION_SLOWDOWN;
      vy *= TREE_COLLISION_SLOWDOWN;
      vy = Math.max(vy, 1);
      if (soundEffects) {
        soundEffects.play('gameAction');
        soundEffects.play('badMove');
        soundEffects.play('shortBoom');
      }
      tryChangeEmoticon();
    }

    // Bomb collision (if near ground and close horizontally)
    const bombHitIndex = bombsRef.current.findIndex(bomb => {
      if (bomb.isExploded) return false;
      return (Math.abs(x - bomb.x) < BOMB_COLLISION_WIDTH / 2) && (y < 1);
    });
    if (bombHitIndex !== -1) {
      bombsRef.current[bombHitIndex].isExploded = true;
      setBombs([...bombsRef.current]);
      vy = BOMB_BOOST_SPEED;
      vx *= 0.8;
      if (soundEffects) {
        soundEffects.play('gameAction');
        soundEffects.play('boom');
      }
      tryChangeEmoticon();
    }

    // Trampoline collision
    const trampolineHitIndex = trampolinesRef.current.findIndex(tramp => {
      if (tramp.isUsed) return false;
      return (Math.abs(x - tramp.x) < (TRAMPOLINE_WIDTH * tramp.scale) / 2) && (y < 1);
    });
    if (trampolineHitIndex !== -1) {
      trampolinesRef.current[trampolineHitIndex].isUsed = true;
      setTrampolines([...trampolinesRef.current]);
      vy = TRAMPOLINE_BOOST;
      vx *= 0.95;
      if (soundEffects) {
        soundEffects.play('gameAction');
        soundEffects.play('boing');
      }
      tryChangeEmoticon();
    }

    // Hole collision (trigger falling)
    const holeHitIndex = holesRef.current.findIndex(hole => {
      return (Math.abs(x - hole.x) < hole.width / 2) && (y < 1);
    });
    if (holeHitIndex !== -1) {
      if (!fallingInHoleRef.current) {
        fallingInHoleRef.current = true;
        vy = -400;
        vx = 0;
        if (soundEffects) {
          soundEffects.play('gameAction');
          soundEffects.play('shortBoom');
        }
        tryChangeEmoticon('😱');
      } else {
        vx = 0;
      }
      if (y < -5) { // fallen far enough to end game
        endGame(x);
        return;
      }
    }

    // Ground bounce (if below ground and not falling into a hole)
    if (y < 0 && !fallingInHoleRef.current) {
      y = 0;
      vy = -vy * FRICTION_COEFF;
      vx *= 0.9;
      if (soundEffects) {
        soundEffects.play('shortBoom');
      }
      tryChangeEmoticon();
      if (Math.abs(vx) < STOP_THRESHOLD && Math.abs(vy) < STOP_THRESHOLD) {
        endGame(x);
        return;
      }
    }

    // ---------------------------
    // 5. ROTATION UPDATE (for ragdoll spin)
    // ---------------------------
    const angleDeg = (Math.atan2(vy, vx) * (180 / Math.PI));
    const blend = 0.15;
    const curRot = projRotationRef.current;
    projRotationRef.current = curRot + blend * (angleDeg - curRot);

    // ---------------------------
    // 6. UPDATE REFS & CAMERA
    // ---------------------------
    velocityRef.current = { vx, vy };
    positionRef.current = { x, y };
    setVelocity({ vx, vy });
    setPosition({ x, y });
    setProjRotation(projRotationRef.current);

    // Simple camera follow (only horizontal)
    if (x > cameraXRef.current + 0.1) {
      cameraXRef.current = x;
      setCameraX(x);
    }

    // UFO update code (left as in your existing logic, adjusted for game coordinates) ...
    // [Your UFO collision and update code here, but use game coordinates (no divide-by-2).]

    requestRef.current = requestAnimationFrame(physicsLoop);
  };

  // ---------------------------
  // SIDE EFFECTS & INTERVALS
  // ---------------------------
  useEffect(() => {
    if (gamePaused || gameState === 'done') {
      cancelAnimationFrame(requestRef.current);
    } else {
      if (gameState === 'angle' || gameState === 'power') {
        requestRef.current = requestAnimationFrame(animLoop);
      } else if (gameState === 'flying') {
        requestRef.current = requestAnimationFrame(physicsLoop);
      }
    }
    return () => cancelAnimationFrame(requestRef.current);
  }, [gameState, gamePaused]);

  // Generate initial terrain obstacles (all in game coordinates)
  useEffect(() => {
    const initTrees = generateTreesInRange(20, INITIAL_WORLD_WIDTH);
    treesRef.current = initTrees;
    setTrees(initTrees);

    const initBombs = generateBombsInRange(40, INITIAL_WORLD_WIDTH);
    bombsRef.current = initBombs;
    setBombs(initBombs);

    const initTrampolines = generateTrampolinesInRange(30, INITIAL_WORLD_WIDTH);
    trampolinesRef.current = initTrampolines;
    setTrampolines(initTrampolines);

    const initHoles = generateHolesInRange(50, INITIAL_WORLD_WIDTH);
    holesRef.current = initHoles;
    setHoles(initHoles);
  }, []);

  // UFO spawning when flying (similar to your original code)
  useEffect(() => {
    if (gameState === 'flying') {
      const initialUfo = {
        x: -100,
        y: 100 + Math.random() * UFO_HEIGHT_RANGE,
        hasHit: false,
        isCollisionCourse: Math.random() < 0.20,
        spawnTime: Date.now(),
        isDeparting: false,
        departureOffset: { x: 0, y: 0 }
      };
      ufosRef.current = [initialUfo];
      setUfos([initialUfo]);

      const spawnInterval = setInterval(() => {
        const newUfo = {
          x: -100,
          y: 100 + Math.random() * UFO_HEIGHT_RANGE,
          hasHit: false,
          isCollisionCourse: Math.random() < 0.20,
          spawnTime: Date.now(),
          isDeparting: false,
          departureOffset: { x: 0, y: 0 }
        };
        ufosRef.current = [...ufosRef.current, newUfo];
        setUfos([...ufosRef.current]);
      }, UFO_SPAWN_INTERVAL);

      return () => {
        clearInterval(spawnInterval);
        ufosRef.current = [];
        setUfos([]);
      };
    }
  }, [gameState]);

  // ---------------------------
  // ACTION HANDLERS
  // ---------------------------
  const handleSelectAngle = () => {
    if (soundEffects) soundEffects.play('gameAction');
    setGameState('power');
  };

  const handleSelectPower = () => {
    if (soundEffects) {
      soundEffects.play('playButton');
      soundEffects.play('boom');
    }
    setGameState('flying');
    const rad = angleRef.current * Math.PI / 180;
    const launchSpeed = (powerRef.current / 100) * MAX_LAUNCH_SPEED;
    const vx = launchSpeed * Math.cos(rad);
    const vy = launchSpeed * Math.sin(rad);
    velocityRef.current = { vx, vy };
    setVelocity({ vx, vy });
  };

  const endGame = (finalX) => {
    setGameState('done');
    setIsTimerActive(false);
  };

  const handleTryAgain = () => {
    if (soundEffects) soundEffects.play('playButton');

    // Reset refs and states
    angleRef.current = 0; angleDirRef.current = 1;
    powerRef.current = 0; powerDirRef.current = 1;
    velocityRef.current = { vx: 0, vy: 0 };
    positionRef.current = { x: 0, y: 0 };
    projRotationRef.current = 0;
    cameraXRef.current = 0;

    setAngle(0);
    setPower(0);
    setVelocity({ vx: 0, vy: 0 });
    setPosition({ x: 0, y: 0 });
    setProjRotation(0);
    setCameraX(0);
    setScore(0);

    // Reset obstacles (you might want to regenerate them entirely)
    const resetTrees = trees.map(tree => ({ ...tree, rotation: (Math.random() * 10) - 5, isKnockedOver: false }));
    setTrees(resetTrees);
    treesRef.current = resetTrees;

    const resetBombs = bombs.map(bomb => ({ ...bomb, isExploded: false }));
    setBombs(resetBombs);
    bombsRef.current = resetBombs;

    const resetTrampolines = trampolines.map(tramp => ({ ...tramp, isUsed: false }));
    setTrampolines(resetTrampolines);
    trampolinesRef.current = resetTrampolines;

    setPosition({ x: 0, y: 0 });
    positionRef.current = { x: 0, y: 0 };
    setVelocity({ vx: 0, vy: 0 });
    velocityRef.current = { vx: 0, vy: 0 };
    setCameraX(0);
    cameraXRef.current = 0;
    fallingInHoleRef.current = false;

    setGameState('angle');
    setIsTimerActive(false);
  };

  // ---------------------------
  // RENDERING
  // ---------------------------
  return (
    <div className="cannon-game-container">
      {gameState === 'done' && (
        <div className="game-over">
          <h2>Game Over!</h2>
          <p>Distance: {score}m</p>
          <p>Best: {highScore}m</p>
          <button onClick={handleTryAgain}>Restart Game</button>
        </div>
      )}

      <div className="cannon-scoreboard">
        <div>SCORE: {score}m</div>
        <div>BEST: {highScore}m</div>
      </div>

      <div className="cannon-game-view">
        <div
          className="cannon-game-world"
          style={{
            transform: `translateX(${-cameraX * SCALE}px)`,
            width: `${(lastGeneratedXRef.current + GENERATION_CHUNK_WIDTH) * SCALE}px`
          }}
        >
          <div
            className="cannon-ground"
            style={{
              width: `${(lastGeneratedXRef.current + GENERATION_CHUNK_WIDTH) * SCALE}px`,
              height: `${GROUND_RENDER_HEIGHT}px`
            }}
          />

          {/* Cannon (rotated by -angle) */}
          {gameState !== 'done' && (
            <div className="cannon-body" style={{ transform: `rotate(${-angle}deg)` }}>
              <div className="cannon-barrel">
                {gameState === 'power' && (
                  <div
                    className="power-indicator"
                    style={{
                      width: `${power}%`,
                      height: '100%',
                      backgroundColor: '#ff4444',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      transition: 'width 0.05s linear'
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Projectile (stick figure) */}
          {(gameState === 'flying' || gameState === 'done') && (
            <div
              className="cannon-projectile"
              style={{
                ...gameToCSS({ x: position.x, y: position.y }),
                transform: `rotate(${projRotation}deg)`
              }}
            >
              <div className="stick-figure">
                <div className="head">{currentEmoticon}</div>
                <div className="body" />
                <div className="arm left" />
                <div className="arm right" />
                <div className="leg left" />
                <div className="leg right" />
              </div>

              {/* Render collision-course UFOs inside the stick figure if desired */}
              {ufos.filter(ufo => ufo.isCollisionCourse).map((ufo, index) => {
                // UFO departure logic (adjusted to game coordinates)
                const shouldDepart = Date.now() - ufo.spawnTime > 3000;
                if (shouldDepart && !ufo.isDeparting) {
                  ufo.isDeparting = true;
                }
                if (ufo.isDeparting) {
                  ufo.departureOffset.x += 5;
                  ufo.departureOffset.y += 3;
                }
                if (ufo.departureOffset.x > window.innerWidth) return null;
                return (
                  <div
                    key={`collision-ufo-${index}`}
                    className="ufo collision-course"
                    style={{
                      position: 'absolute',
                      left: ufo.departureOffset.x,
                      bottom: ufo.departureOffset.y,
                      filter: 'hue-rotate(90deg)',
                      transition: ufo.isDeparting ? 'all 0.1s linear' : 'none'
                    }}
                  >
                    <div className="ufo-dome" />
                    <div className="ufo-body" />
                    <div className="ufo-light" />
                  </div>
                );
              })}
            </div>
          )}

          {/* Render normal UFOs */}
          {ufos.filter(ufo => !ufo.isCollisionCourse).map((ufo, index) => (
            <div
              key={`ufo-${index}`}
              className="ufo"
              style={{
                left: `${ufo.x * SCALE}px`,
                bottom: `${ufo.y * SCALE}px`,
                transform: `translateX(${ufo.hasHit ? 100 : 0}px)`
              }}
            >
              <div className="ufo-dome" />
              <div className="ufo-body" />
              <div className="ufo-light" />
            </div>
          ))}

          {/* Render Trees */}
          {trees.map((tree, index) => (
            <div
              key={index}
              className="tree"
              style={{
                left: `${tree.x * SCALE}px`,
                ...{ transform: `scale(${tree.scale}) rotate(${tree.rotation}deg)` },
                transition: 'transform 0.3s ease-out'
              }}
            >
              <div className="tree-trunk" />
              <div className="tree-crown" />
            </div>
          ))}

          {/* Render Bombs */}
          {bombs.map((bomb, index) => (
            <div
              key={`bomb-${index}`}
              className={`bomb ${bomb.isExploded ? 'exploded' : ''}`}
              style={{
                left: `${bomb.x * SCALE}px`,
                bottom: `${bomb.y * SCALE}px`,
                transform: `scale(${bomb.scale})`
              }}
            >
              {!bomb.isExploded && '💣'}
              {bomb.isExploded && <div className="explosion" />}
            </div>
          ))}

          {/* Render Trampolines */}
          {trampolines.map((tramp, index) => (
            <div
              key={`tramp-${index}`}
              className={`trampoline ${tramp.isUsed ? 'used' : ''}`}
              style={{
                left: `${(tramp.x + 35) * SCALE}px`,
                transform: `scale(${tramp.scale})`
              }}
            >
              <div className="trampoline-base" />
              <div className="trampoline-spring" />
            </div>
          ))}

          {/* Render Holes */}
          {holes.map((hole, index) => (
            <div
              key={`hole-${index}`}
              className="hole"
              style={{
                left: `${(hole.x - 25) * SCALE}px`,
                width: `${hole.width * SCALE}px`
              }}
            >
              <div className="hole-surface" />
              <div className="hole-depth" />
            </div>
          ))}
        </div>
      </div>

      {(gameState === 'angle' || gameState === 'power') && (
        <div className="cannon-ui-panel">
          {gameState === 'angle' && (
            <button onClick={handleSelectAngle} className="cannon-button">
              Select Angle {Math.floor(angle)}°
            </button>
          )}
          {gameState === 'power' && (
            <button onClick={handleSelectPower} className="cannon-button">
              Select Power {Math.floor(power)}%
            </button>
          )}
        </div>
      )}

      {/* Tracking bubble when projectile is off-screen */}
      {gameState === 'flying' && (
        <div
          className="tracking-bubble"
          style={{
            left: `${Math.min(Math.max(position.x * SCALE - cameraX * SCALE, 100), window.innerWidth - 100) + 40}px`
          }}
        >
          <div className="arrow" />
          <div className="mini-figure">
            <div
              className="stick-figure"
              style={{ transform: `rotate(${projRotation}deg)` }}
            >
              <div className="head">{currentEmoticon}</div>
              <div className="body" />
              <div className="arm left" />
              <div className="arm right" />
              <div className="leg left" />
              <div className="leg right" />
            </div>
          </div>
          <div className="height">{Math.floor(position.y)}m</div>
        </div>
      )}
    </div>
  );
}

export default CannonLaunchGame;
